import { useState, memo } from 'react';
import { LandingNavbar, LandingFooter } from '@/components/landing';
import { useCal } from '@/hooks/useCal';
import { 
  ArrowRight, 
  BarChart2, 
  Zap, 
  TrendingUp, 
  TrendingDown,
  ShoppingBag, 
  AlertTriangle,
  Activity,
  Package,
  Filter,
  Download,
  Calendar,
  ChevronDown,
  AlertCircle,
  Target,
  List
} from 'lucide-react';

// --- ReflowAI Design System ---
const COLORS = {
  brilliance: '#FCFDFD',
  springtime: '#EDEFF3', 
  windweaver: '#C6D1D7',
  thistle: '#9FA0B5',
  sapphire: '#2F7EDA',
  blackwater: '#555663',
  darkText: '#0F172A',
  danger: '#EF4444',
  success: '#10B981',
  warning: '#F59E0B'
};

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      --font-primary: 'Plus Jakarta Sans', sans-serif;
    }

    body {
      font-family: var(--font-primary);
      color: ${COLORS.blackwater};
      background-color: ${COLORS.brilliance};
      overflow-x: hidden;
      max-width: 100vw;
    }
    
    * {
      box-sizing: border-box;
    }

    /* Halo Gradient Background */
    .halo-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      pointer-events: none;
      overflow: hidden;
    }

    .halo-gradient {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: pulse-halo 10s ease-in-out infinite;
    }

    @keyframes pulse-halo {
      0%, 100% { transform: scale(1); opacity: 0.4; }
      50% { transform: scale(1.1); opacity: 0.6; }
    }

    /* Glass Effects */
    .glass-panel {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 10px 40px -10px rgba(47, 126, 218, 0.05);
    }
    
    .glass-card {
      background: linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(245,247,250,0.6) 100%);
      backdrop-filter: blur(24px);
      border: 1px solid ${COLORS.windweaver};
      transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    }

    .glass-card:hover {
      border-color: ${COLORS.sapphire};
      transform: translateY(-2px);
      box-shadow: 0 15px 30px -5px rgba(47, 126, 218, 0.1);
    }

    .glass-pill {
      background: rgba(252, 253, 253, 0.8); 
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 4px 10px -2px rgba(0,0,0,0.05);
    }

    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
  `}</style>
);

// --- Memoized Sub-Components ---

const StatCard = memo(({ title, value, subtext, trend, trendLabel, icon: Icon, alert }: any) => (
  <div className={`glass-card rounded-[20px] sm:rounded-[24px] p-4 sm:p-6 flex flex-col justify-between h-full ${alert ? 'border-red-200 bg-red-50/30' : ''}`}>
    <div className="flex justify-between items-start mb-3 sm:mb-4">
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center ${alert ? 'bg-red-100 text-red-600' : 'bg-[#EDEFF3] text-[#555663]'}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      {trend && (
        <div className={`flex items-center text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap ${trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div>
      <h3 className="text-[#9FA0B5] text-xs font-bold uppercase tracking-wider mb-1">{title}</h3>
      <div className="text-2xl sm:text-3xl font-bold text-[#0F172A] mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-[#555663]">{subtext}</div>
      {trendLabel && <div className="text-xs text-[#9FA0B5] mt-2 font-medium">{trendLabel}</div>}
    </div>
  </div>
));

const InsightBanner = memo(() => (
  <div className="glass-card rounded-[24px] sm:rounded-[30px] p-5 sm:p-6 md:p-8 shadow-xl relative overflow-hidden mb-6 sm:mb-8 border-[#C6D1D7]">
    <div className="absolute top-0 right-0 w-64 h-64 bg-[#2F7EDA] opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
    <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#9FA0B5] opacity-5 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4"></div>
    
    <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2F7EDA]/10 backdrop-blur-md flex items-center justify-center flex-shrink-0 border border-[#2F7EDA]/20">
        <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-[#2F7EDA]" />
      </div>
      <div className="flex-grow">
        <div className="text-xs font-bold text-[#9FA0B5] uppercase tracking-widest mb-1 sm:mb-2">Executive Summary • Week 42</div>
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold leading-tight text-[#0F172A]">
          Sales fell <span className="text-[#EF4444] border-b-2 border-[#EF4444]/30">9%</span> mainly because 2 top SKUs went OOS in high-velocity clusters.
        </h2>
        <p className="text-[#555663] mt-2 text-sm sm:text-base leading-relaxed">
          Visibility stayed stable, but availability gaps in Indiranagar & HSR Layout drove the decline.
        </p>
      </div>
    </div>
  </div>
));

const PriorityActionItem = memo(({ rank, title, impact, category }: any) => {
  const getCategoryColor = (cat: string) => {
    switch(cat) {
      case 'Stock': return 'text-purple-600 bg-purple-100';
      case 'Pricing': return 'text-blue-600 bg-blue-100';
      case 'Ads': return 'text-amber-600 bg-amber-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-[16px] sm:rounded-[20px] bg-white border border-[#EDEFF3] hover:border-[#2F7EDA] hover:shadow-md transition-all group cursor-pointer relative overflow-hidden active:scale-[0.99] w-full">
      
      {/* Content Side */}
      <div className="flex items-start gap-2 sm:gap-3 flex-grow min-w-0">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#2F7EDA]/10 text-[#2F7EDA] font-bold flex items-center justify-center flex-shrink-0 mt-0.5 text-xs sm:text-sm">
            {rank}
        </div>
        
        <div className="min-w-0 flex-grow">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
                <span className={`text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full uppercase ${getCategoryColor(category)}`}>
                    {category}
                </span>
                <span className="text-[9px] sm:text-[10px] font-bold text-[#EF4444] flex items-center bg-red-50 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                    <AlertCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" /> High Impact
                </span>
            </div>
            <h4 className="text-xs sm:text-sm font-bold text-[#0F172A] leading-snug group-hover:text-[#2F7EDA] transition-colors line-clamp-2 break-words">
                {title}
            </h4>
        </div>
      </div>

      {/* Impact Side - Adaptive */}
      <div className="flex items-center justify-between sm:block sm:text-right pl-9 sm:pl-4 border-t sm:border-t-0 sm:border-l border-[#EDEFF3] pt-2 sm:pt-0 mt-1 sm:mt-0 sm:min-w-[100px] flex-shrink-0">
        <div className="text-xs text-[#9FA0B5] uppercase font-bold sm:hidden">Revenue Impact</div>
        <div className="hidden sm:block text-[10px] text-[#9FA0B5] uppercase font-bold tracking-wider mb-0.5">Recovery</div>
        <div className="text-xs sm:text-sm font-bold text-[#10B981] whitespace-nowrap">{impact}</div>
      </div>

       {/* Arrow Overlay (Desktop) */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 hidden lg:flex bg-white shadow-lg rounded-full p-2 border border-[#EDEFF3] z-10">
          <ArrowRight className="w-4 h-4 text-[#2F7EDA]" />
      </div>
    </div>
  );
});

const DriverBar = memo(({ label, value, percentage, isNegative }: any) => {
    return (
        <div className="mb-3 sm:mb-4 last:mb-0">
            <div className="flex justify-between items-start gap-2 text-xs sm:text-sm mb-1">
                <span className="font-semibold text-[#555663] truncate">{label}</span>
                <span className={`font-bold whitespace-nowrap flex-shrink-0 ${isNegative ? 'text-red-500' : 'text-green-600'}`}>
                   {isNegative ? '' : '+'}{value} ({Math.abs(percentage)}%)
                </span>
            </div>
            <div className="w-full bg-[#EDEFF3] rounded-full h-2 overflow-hidden">
                <div 
                    className={`h-full rounded-full ${isNegative ? 'bg-red-500' : 'bg-green-500'}`} 
                    style={{ width: `${Math.abs(percentage)}%` }}
                ></div>
            </div>
        </div>
    )
});

// --- Chart Component (Dynamic Waterfall) ---
const WaterfallChart = memo(() => {
    const maxValue = 50;
    
    const bars = [
        { label: "Prev", value: 46.7, type: "total", color: "bg-gray-300", text: "text-gray-500", displayVal: "46.7L" },
        { label: "Organic", value: 1.2, type: "positive", color: "bg-green-500", text: "text-green-600", displayVal: "+1.2L", offset: 46.7 },
        { label: "Ads", value: 0.3, type: "positive", color: "bg-green-400", text: "text-green-600", displayVal: "+0.3L", offset: 47.9 },
        { label: "OOS", value: 4.5, type: "negative", color: "bg-red-500", text: "text-red-600", displayVal: "-4.5L", offset: 43.7 },
        { label: "Price", value: 1.2, type: "negative", color: "bg-red-300", text: "text-red-500", displayVal: "-1.2L", offset: 42.5 },
        { label: "Current", value: 42.5, type: "total", color: "bg-[#2F7EDA]", text: "text-[#2F7EDA]", displayVal: "42.5L" }
    ];

    return (
        <div className="relative h-48 sm:h-56 md:h-64 w-full flex items-end justify-between px-1 sm:px-2 pt-6 sm:pt-8 pb-5 sm:pb-6">
            {/* Grid Lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none z-0">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-full border-t border-dashed border-gray-200 h-0"></div>
                ))}
            </div>

            {bars.map((bar, i) => {
                const heightPercent = (bar.value / maxValue) * 100;
                let bottomPercent = 0;

                if (bar.type === 'total') {
                    bottomPercent = 0;
                } else if (bar.type === 'positive') {
                    bottomPercent = ((bar.offset || 0) / maxValue) * 100;
                } else if (bar.type === 'negative') {
                    if (bar.label === 'OOS') bottomPercent = ((48.2 - 4.5) / maxValue) * 100;
                    if (bar.label === 'Price') bottomPercent = ((43.7 - 1.2) / maxValue) * 100;
                }

                return (
                    <div key={i} className="relative w-1/6 h-full z-10 mx-0.5 sm:mx-1 flex flex-col justify-end group">
                         <div 
                            className={`w-full rounded-sm sm:rounded-md transition-all duration-500 hover:opacity-80 ${bar.color}`}
                            style={{ 
                                height: `${heightPercent}%`, 
                                marginBottom: `${bottomPercent}%`
                            }}
                         >
                             <div className={`absolute -top-5 sm:-top-6 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-bold ${bar.text} whitespace-nowrap opacity-100`}>
                                 {bar.displayVal}
                             </div>
                         </div>
                         <div className="absolute -bottom-5 sm:-bottom-6 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-semibold text-[#555663]">
                             {bar.label}
                         </div>
                    </div>
                );
            })}
        </div>
    );
});

// --- Why Performance Matters Section ---
const WhyPerformanceMatters = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto glass-panel rounded-[48px] p-12 bg-white/40">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-4">Why Performance Intelligence Matters</h2>
          <p className="text-[#555663] max-w-2xl mx-auto">
            Quick Commerce brands lose an average of 15% revenue due to undetected issues. Real-time performance tracking turns data into actionable insights.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { 
              icon: TrendingDown, 
              title: "Revenue Leakage", 
              desc: "Identify exactly where you're bleeding money - whether it's OOS, pricing gaps, or poor ad performance. Stop guessing, start knowing." 
            },
            { 
              icon: Target, 
              title: "Prioritized Actions", 
              desc: "Not all problems are equal. Our AI ranks issues by revenue impact so you fix the biggest leaks first and maximize ROI." 
            },
            { 
              icon: Activity, 
              title: "Real-Time Alerts", 
              desc: "Get instant notifications when KPIs drop below threshold. React in hours, not days, and prevent small issues from becoming big losses." 
            }
          ].map((feature, i) => (
            <div key={i} className="text-center p-6 rounded-3xl hover:bg-white/50 transition-colors">
              <div className="w-16 h-16 rounded-2xl bg-[#FEF3C7] flex items-center justify-center mx-auto mb-6 text-[#F59E0B]">
                <feature.icon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-3">{feature.title}</h3>
              <p className="text-[#555663] leading-relaxed text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Component ---
const PerformanceCTA = () => {
  const { openCalModal } = useCal();

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 text-center">
      <div className="max-w-4xl mx-auto glass-panel rounded-[32px] sm:rounded-[40px] md:rounded-[60px] p-6 sm:p-10 md:p-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#2F7EDA] to-[#4F46E5]"></div>
        
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4 sm:mb-6 md:mb-8">Drive Growth with Data Intelligence</h2>
        <p className="text-base sm:text-lg text-[#555663] mb-6 sm:mb-8 md:mb-10 max-w-2xl mx-auto">Get real-time performance insights and actionable recommendations to boost your revenue.</p>
        
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide bg-[#2F7EDA] text-white hover:opacity-90 hover:-translate-y-0.5"
            style={{ boxShadow: '0 10px 30px -5px rgba(47, 126, 218, 0.4)' }}
          >
            Schedule a Call
          </button>
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide bg-white/50 text-[#555663] border border-[#C6D1D7] hover:opacity-90 hover:-translate-y-0.5"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            Book a Demo
          </button>
        </div>
      </div>
    </section>
  );
};

// --- Data Constants ---

const KPI_DATA = [
  { title: "Total Sales (GMV)", value: "₹42.5L", subtext: "vs ₹46.7L last week", trend: -9.2, trendLabel: "Impacted by OOS", icon: ShoppingBag, alert: false },
  { title: "Revenue at Risk", value: "₹3.8L", subtext: "Lost opportunity this week", trend: +12, trendLabel: "Critical level", icon: AlertTriangle, alert: true },
  { title: "Marketing Spend", value: "₹85K", subtext: "ROAS: 3.2x (Healthy)", trend: -2.4, trendLabel: "Efficiency improved", icon: Target, alert: false },
  { title: "Availability", value: "88%", subtext: "Target: 95%", trend: -5.0, trendLabel: "Major dip in Bangalore", icon: Package, alert: false },
];

const ACTION_ITEMS = [
  { rank: "1", title: "Restock Oat Milk in Indiranagar Dark Store", impact: "₹1.2L", category: "Stock" },
  { rank: "2", title: "Price Match Zepto on Protein Bars", impact: "₹0.8L", category: "Pricing" },
  { rank: "3", title: "Low Ad Rank on 'Healthy Snacks' - Increase Bid", impact: "₹0.5L", category: "Ads" },
  { rank: "4", title: "Stock Mismatch in Koramangala Hub", impact: "₹0.2L", category: "Stock" }
];

const DRIVER_DATA = [
    { label: "Organic Demand", value: "1.2L", percentage: 12, isNegative: false },
    { label: "Ad Performance", value: "0.3L", percentage: 3, isNegative: false },
    { label: "Competitor Pricing", value: "-1.2L", percentage: -8, isNegative: true },
    { label: "Out of Stock (OOS)", value: "-4.5L", percentage: -22, isNegative: true },
];

const TABS = ['Overview', 'Drivers Breakdown', 'SKU Performance', 'Location Analysis'];

// --- Main Page ---

export function PerformanceLandingPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange] = useState('Last 7 Days');

  return (
    <div className="overflow-x-hidden">
      <GlobalStyles />
      
      {/* Background Elements */}
      <div className="halo-container">
        <div className="halo-gradient w-[800px] h-[800px] bg-[#2F7EDA] top-[-20%] left-[-10%] opacity-20"></div>
        <div className="halo-gradient w-[600px] h-[600px] bg-[#4F46E5] bottom-[-10%] right-[-10%] opacity-15"></div>
      </div>

      <LandingNavbar />

      <main>
        <div className="min-h-screen pb-12 sm:pb-16 md:pb-20 pt-24 sm:pt-28 md:pt-32 px-4 sm:px-6 md:px-8 max-w-7xl mx-auto relative">
          
          {/* --- Header Section --- */}
          <header className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 sm:mb-8 md:mb-10 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-bold uppercase tracking-wider mb-3 sm:mb-4">
                <BarChart2 className="w-3 h-3" /> Performance Intelligence
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F172A] mb-2 tracking-tight">Performance Dashboard</h1>
              <p className="text-[#555663] text-base sm:text-lg">Your command center for growth, efficiency, and issue resolution.</p>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto">
                <div className="glass-pill px-3 sm:px-4 py-2 rounded-full flex items-center gap-2 cursor-pointer hover:bg-white transition-colors flex-1 sm:flex-initial">
                    <Calendar className="w-4 h-4 text-[#555663]" />
                    <span className="text-xs sm:text-sm font-bold text-[#0F172A] truncate">{timeRange}</span>
                    <ChevronDown className="w-4 h-4 text-[#9FA0B5] flex-shrink-0" />
                </div>
                <button aria-label="Filter" className="w-10 h-10 rounded-full glass-pill flex items-center justify-center hover:bg-white text-[#555663] flex-shrink-0">
                    <Filter className="w-4 h-4" />
                </button>
                <button aria-label="Download Report" className="w-10 h-10 rounded-full bg-[#2F7EDA] text-white flex items-center justify-center shadow-lg hover:bg-[#1a5bb8] transition-colors flex-shrink-0">
                    <Download className="w-4 h-4" />
                </button>
            </div>
          </header>

          <InsightBanner />

          {/* --- KPI Grid --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
            {KPI_DATA.map((kpi, index) => (
              <StatCard 
                key={index}
                {...kpi}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start mb-8 sm:mb-12 md:mb-16">
            
            {/* --- Left Column: Drivers & Breakdown (8 cols) --- */}
            <div className="lg:col-span-8 flex flex-col gap-6 sm:gap-8 w-full">
                
                {/* Tabs */}
                <div className="flex items-center gap-1 sm:gap-2 border-b border-[#C6D1D7] pb-1 overflow-x-auto scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0">
                    {TABS.map((tab) => {
                        const key = tab.toLowerCase().split(' ')[0];
                        return (
                            <button 
                                key={tab}
                                onClick={() => setActiveTab(key)}
                                className={`px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold rounded-t-xl transition-colors whitespace-nowrap flex-shrink-0 ${
                                    activeTab === key
                                    ? 'bg-[#2F7EDA]/10 text-[#2F7EDA] border-b-2 border-[#2F7EDA]' 
                                    : 'text-[#0F172A] hover:text-[#2F7EDA]'
                                }`}
                            >
                                {tab}
                            </button>
                        );
                    })}
                </div>

                {/* Dynamic Content Area */}
                <div className="glass-panel rounded-[24px] sm:rounded-[32px] p-4 sm:p-6 md:p-8 min-h-[400px] sm:min-h-[500px] flex flex-col w-full overflow-hidden">
                    
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Sales Bridge Analysis</h3>
                        <div className="text-xs sm:text-sm text-[#9FA0B5] flex items-center gap-2 w-fit">
                            <Activity className="w-3 h-3 sm:w-4 sm:h-4" /> <span className="whitespace-nowrap">Real-time attribution</span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 mb-6 sm:mb-8">
                        {/* Visual Chart */}
                        <div className="w-full overflow-hidden">
                           <WaterfallChart />
                        </div>

                        {/* Legend/Explanation */}
                        <div>
                            <h4 className="text-xs sm:text-sm font-bold text-[#0F172A] mb-3 sm:mb-4 uppercase tracking-wider">Growth Drivers</h4>
                            <div className="space-y-3 sm:space-y-4">
                                {DRIVER_DATA.map((driver, index) => (
                                    <DriverBar key={index} {...driver} />
                                ))}
                            </div>
                            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-[#EDEFF3] rounded-xl sm:rounded-2xl text-xs sm:text-sm text-[#555663] leading-relaxed border border-white/50">
                                <span className="font-bold text-[#0F172A]">Insight:</span> <span className="text-green-600 font-bold">+Organic</span> demand is healthy, but <span className="text-red-600 font-bold">-OOS</span> events in Indiranagar are the primary drag on growth this week.
                            </div>
                        </div>
                    </div>

                    {/* Drill Down Table Snippet */}
                    <div className="mt-auto pt-6 sm:pt-8 border-t border-[#EDEFF3]">
                         <div className="flex items-center justify-between mb-3 sm:mb-4">
                            <h4 className="text-xs sm:text-sm font-bold text-[#0F172A]">Top Impacted SKUs</h4>
                            <span className="text-[10px] text-[#9FA0B5] sm:hidden">← Scroll →</span>
                         </div>
                         <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                            <div className="min-w-[500px] rounded-xl sm:rounded-2xl border border-[#EDEFF3] overflow-hidden shadow-sm">
                               <table className="w-full text-left text-xs sm:text-sm">
                                   <thead className="bg-[#EDEFF3] text-[#555663]">
                                       <tr>
                                           <th className="p-2 sm:p-4 font-semibold whitespace-nowrap">Product Name</th>
                                           <th className="p-2 sm:p-4 font-semibold whitespace-nowrap">Status</th>
                                           <th className="p-2 sm:p-4 font-semibold whitespace-nowrap">Impact</th>
                                           <th className="p-2 sm:p-4 font-semibold whitespace-nowrap">Action</th>
                                       </tr>
                                   </thead>
                                   <tbody className="divide-y divide-[#EDEFF3] bg-white">
                                       <tr className="hover:bg-gray-50 transition-colors">
                                           <td className="p-2 sm:p-4 font-bold text-[#0F172A] whitespace-nowrap">Oat Milk (Barista) 1L</td>
                                           <td className="p-2 sm:p-4 whitespace-nowrap"><span className="px-2 py-1 bg-red-100 text-red-600 rounded-md text-xs font-bold">OOS</span></td>
                                           <td className="p-2 sm:p-4 text-red-600 font-bold whitespace-nowrap">-₹1.2L</td>
                                           <td className="p-2 sm:p-4 whitespace-nowrap"><span className="text-[#2F7EDA] font-bold cursor-pointer hover:underline">Restock</span></td>
                                       </tr>
                                       <tr className="hover:bg-gray-50 transition-colors">
                                           <td className="p-2 sm:p-4 font-bold text-[#0F172A] whitespace-nowrap">Almond Butter</td>
                                           <td className="p-2 sm:p-4 whitespace-nowrap"><span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs font-bold">Low</span></td>
                                           <td className="p-2 sm:p-4 text-red-500 font-bold whitespace-nowrap">-₹0.4L</td>
                                           <td className="p-2 sm:p-4 whitespace-nowrap"><span className="text-[#2F7EDA] font-bold cursor-pointer hover:underline">Fix</span></td>
                                       </tr>
                                   </tbody>
                               </table>
                            </div>
                         </div>
                    </div>
                </div>
            </div>

            {/* --- Right Column: Priority Actions (4 cols) --- */}
            <div className="lg:col-span-4 w-full">
                <div className="glass-card rounded-[24px] sm:rounded-[32px] p-4 sm:p-6 flex flex-col relative overflow-hidden min-h-[400px] lg:h-full">
                     <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
                     
                     <div className="flex items-center justify-between mb-4 sm:mb-6 mt-2">
                        <h3 className="text-base sm:text-lg font-bold text-[#0F172A]">Priority Actions</h3>
                        <div className="w-8 h-8 rounded-full bg-[#EDEFF3] flex items-center justify-center text-[#555663] flex-shrink-0">
                            <List className="w-4 h-4" />
                        </div>
                     </div>
                     
                     <p className="text-xs sm:text-sm text-[#555663] mb-4 sm:mb-6">
                        Resolving these 4 issues will recover <span className="font-bold text-[#0F172A]">78%</span> of your revenue at risk.
                     </p>

                     <div className="space-y-2 sm:space-y-3 flex-grow overflow-y-auto overflow-x-hidden scrollbar-hide">
                        {ACTION_ITEMS.map((item, index) => (
                          <PriorityActionItem key={index} {...item} />
                        ))}
                     </div>
                     
                     <div className="mt-4 sm:mt-6 border-t border-[#EDEFF3] pt-4 sm:pt-6 flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 text-xs sm:text-sm font-bold text-[#555663]">
                        <span>Pending Actions: 12</span>
                        <button className="flex items-center gap-1 text-[#2F7EDA] hover:underline w-fit">
                            View All <ArrowRight className="w-3 h-3" />
                        </button>
                     </div>
                </div>
            </div>
          </div>

        </div>
      </main>

      <WhyPerformanceMatters />

      <PerformanceCTA />

      <LandingFooter />
    </div>
  );
}
